import { BarbellIcon } from "@/components/icons/barbell-icon";
import { BreadIcon } from "@/components/icons/bread-icon";
import { DropIcon } from "@/components/icons/drop-icon";
import { FlameIcon } from "@/components/icons/flame-icon";
import { InlineEditButton } from "@/components/ui/buttons/InlineEditButton";
import { useGetCalories } from "@/hooks/requests/nutritional-requirement/use-get-calories";
import { useGetMacros } from "@/hooks/requests/nutritional-requirement/use-get-macros";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";

interface NutritionSummaryCardProps {
  variant: "calories" | "macros";
  onAmend: () => void;
  className?: string;
}

export const NutritionSummaryCard = ({
  variant,
  onAmend,
  className,
}: NutritionSummaryCardProps) => {
  const { calories } = useGetCalories({ enabled: variant === "calories" });
  const { macros } = useGetMacros({ enabled: variant === "macros" });

  return (
    <div
      className={cn(
        "relative w-full rounded-[32px] border border-[rgba(237,237,237,0.08)] bg-white px-5 py-6",
        "shadow-[0px_8px_32.35px_rgba(64,0,255,0.1)]",
        className,
      )}
    >
      {variant === "calories" ? (
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FlameIcon className="text-os-brand h-5 w-[17px]" />
              <p className={cn(textVariants.title1, "text-black")}>
                {calories?.dailyCalories?.toLocaleString() ?? "—"} kcals / day
              </p>
            </div>
            <InlineEditButton onClick={onAmend} />
          </div>
          <p className="text-base font-bold text-black opacity-50">
            {calories?.weeklyCalories?.toLocaleString() ?? "—"} kcals / week
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <p className={cn(textVariants.title2)}>Macros Intake</p>
            <InlineEditButton onClick={onAmend} />
          </div>
          <div className="flex flex-col">
            <MacroRow
              icon={<BarbellIcon className="text-os-brand" />}
              label="Protein"
              grams={macros?.proteinGrams}
              percentage={macros?.proteinPercentage}
            />
            <MacroRow
              icon={<BreadIcon className="text-os-brand" />}
              label="Carbs"
              grams={macros?.carbsGrams}
              percentage={macros?.carbsPercentage}
            />
            <MacroRow
              icon={<DropIcon className="text-os-brand" />}
              label="Fats"
              grams={macros?.fatGrams}
              percentage={macros?.fatPercentage}
            />
          </div>
        </div>
      )}
    </div>
  );
};

interface MacroRowProps {
  icon: React.ReactNode;
  label: string;
  grams?: number;
  percentage?: number;
}

const MacroRow = ({ icon, label, grams, percentage }: MacroRowProps) => (
  <div className="flex items-center justify-between py-[13px]">
    <div className="flex items-center gap-2">
      <span className="size-6 shrink-0">{icon}</span>
      <p className="text-base font-bold text-black">{label}</p>
    </div>
    <p className={cn(textVariants.bodySmall, "text-black")}>
      {grams ?? "—"} g ({percentage ?? "—"}%)
    </p>
  </div>
);
